import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Search, ShoppingBag, CreditCard, Truck, Shield, User, ChevronDown, ChevronUp } from 'lucide-react';

const HelpCenter = () => {
  const { themeColors } = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const containerStyle = {
    minHeight: 'calc(100vh - 120px)',
    backgroundColor: themeColors.background,
    padding: '40px 20px'
  };

  const contentStyle = {
    maxWidth: '1000px',
    margin: '0 auto'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '60px'
  };

  const titleStyle = {
    color: themeColors.text,
    fontSize: '48px',
    fontWeight: 'bold',
    marginBottom: '20px'
  };

  const subtitleStyle = {
    color: themeColors.textSecondary,
    fontSize: '20px',
    lineHeight: '1.6',
    maxWidth: '600px',
    margin: '0 auto'
  };

  const searchBoxStyle = {
    position: 'relative',
    maxWidth: '600px',
    margin: '0 auto 60px'
  };

  const searchInputStyle = {
    width: '100%',
    padding: '16px 50px 16px 20px',
    borderRadius: '12px',
    border: `2px solid ${themeColors.border}`,
    fontSize: '16px',
    backgroundColor: themeColors.cardBackground,
    color: themeColors.text
  };

  const searchIconStyle = {
    position: 'absolute',
    right: '20px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: themeColors.textSecondary
  };

  const sectionStyle = {
    marginBottom: '60px'
  };

  const sectionTitleStyle = {
    color: themeColors.text,
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '24px'
  };

  const categoriesGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px',
    marginBottom: '40px'
  };

  const categoryCardStyle = {
    backgroundColor: themeColors.cardBackground,
    borderRadius: '12px',
    padding: '24px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
    border: `1px solid ${themeColors.border}`
  };

  const iconWrapperStyle = {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: themeColors.primary + '15',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 12px'
  };

  const faqItemStyle = {
    backgroundColor: themeColors.cardBackground,
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '12px',
    cursor: 'pointer',
    border: `1px solid ${themeColors.border}`,
    transition: 'box-shadow 0.2s'
  };

  const faqQuestionStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: themeColors.text,
    fontSize: '16px',
    fontWeight: '600'
  };

  const faqAnswerStyle = {
    color: themeColors.textSecondary,
    fontSize: '15px',
    lineHeight: '1.7',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: `1px solid ${themeColors.border}`
  };

  const ctaCardStyle = {
    backgroundColor: themeColors.primary + '10',
    borderRadius: '16px',
    padding: '32px',
    textAlign: 'center',
    border: `2px solid ${themeColors.primary}30`
  };

  const buttonStyle = {
    backgroundColor: themeColors.primary,
    color: '#ffffff',
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '16px',
    transition: 'opacity 0.2s'
  };

  const categories = [
    { id: 'getting-started', icon: User, title: 'Getting Started', description: 'Account setup, profile tips, and first steps' },
    { id: 'buying', icon: ShoppingBag, title: 'Buying Items', description: 'How to find, purchase, and receive items' },
    { id: 'selling', icon: CreditCard, title: 'Selling Items', description: 'Listing, pricing, and managing your shop' },
    { id: 'shipping', icon: Truck, title: 'Shipping & Delivery', description: 'Shipping options, tracking, and delivery' },
    { id: 'payments', icon: CreditCard, title: 'Payments & Fees', description: 'Payment methods, fees, and payouts' },
    { id: 'safety', icon: Shield, title: 'Safety & Trust', description: 'Staying safe and resolving issues' }
  ];

  const faqs = {
    'getting-started': [
      {
        question: 'How do I create an account?',
        answer: 'Tap "Sign Up" on the homepage, enter your email, choose a username and password, and you\'re ready to go. You can also add a profile photo and bio to help other users get to know you.'
      },
      {
        question: 'Is BabyResell free to use?',
        answer: 'Creating an account and browsing items is completely free. We only charge a small transaction fee (8%) when you successfully sell an item. There are no listing fees.'
      },
      {
        question: 'How do I edit my profile?',
        answer: 'Go to your Profile page by clicking your avatar in the header. From there you can update your photo, bio, location, and other details.'
      }
    ],
    'buying': [
      {
        question: 'How do I find items I\'m looking for?',
        answer: 'Use the search bar on the homepage or browse by category. You can filter by age group, condition, price range, brand, and more. Save items you like by clicking the heart icon.'
      },
      {
        question: 'How does purchasing work?',
        answer: 'When you find an item you want, click "Buy Now" on the item detail page. You\'ll enter your shipping address and payment information. Your payment is held securely until you confirm receipt of the item.'
      },
      {
        question: 'What if the item isn\'t as described?',
        answer: 'If an item arrives significantly different from its listing, you can open a dispute within 48 hours of delivery. Our team will review the case and work toward a fair resolution, which may include a full refund.'
      },
      {
        question: 'Can I message a seller before buying?',
        answer: 'Yes! Click the "Message Seller" button on any item listing to ask questions about condition, sizing, or anything else before you commit to buying.'
      }
    ],
    'selling': [
      {
        question: 'How do I list an item for sale?',
        answer: 'Click "Create Listing" in the navigation, then fill out the details: title, description, photos (up to 8), category, condition, age group, and price. Add relevant tags to help buyers find your item.'
      },
      {
        question: 'How should I price my items?',
        answer: 'Research similar items on the platform to see competitive pricing. Items in "Like New" condition with original packaging typically sell for 50-70% of retail. Be honest about condition — it builds trust and leads to better reviews.'
      },
      {
        question: 'How do I get paid after a sale?',
        answer: 'After the buyer confirms receipt (or after the automatic release period), your earnings minus the platform fee are released to your connected payment account. You\'ll need to complete Stripe seller onboarding to receive payouts.'
      },
      {
        question: 'What items are not allowed?',
        answer: 'You cannot list recalled products, expired car seats, items with damaged safety features, counterfeit goods, or non-baby-related items. See our Safety page for the full prohibited items list.'
      }
    ],
    'shipping': [
      {
        question: 'How does shipping work?',
        answer: 'Sellers are responsible for shipping items to buyers. When creating a listing, you can set your shipping cost or offer local pickup. After a sale, ship the item and enter the tracking number so the buyer can follow the delivery.'
      },
      {
        question: 'Which carriers can I use?',
        answer: 'We support USPS, UPS, FedEx, and DHL. Choose whatever works best for your item size and budget. We recommend USPS Priority Mail for most baby items as it includes insurance and tracking.'
      },
      {
        question: 'What if my item gets lost or damaged in shipping?',
        answer: 'If an item is lost or arrives damaged, contact us through the dispute process. We recommend sellers use tracked shipping and purchase insurance for higher-value items.'
      }
    ],
    'payments': [
      {
        question: 'What payment methods are accepted?',
        answer: 'We accept all major credit and debit cards through our secure Stripe payment processing. Apple Pay and Google Pay support is coming soon.'
      },
      {
        question: 'What fees does BabyResell charge?',
        answer: 'We charge an 8% platform fee on completed sales. This covers payment processing, platform maintenance, customer support, and buyer protection. There are no listing fees or monthly subscription costs.'
      },
      {
        question: 'When do I receive my payout?',
        answer: 'Payouts are processed after the buyer confirms delivery or after the 48-hour automatic release window. Funds typically appear in your bank account within 2-5 business days depending on your bank.'
      }
    ],
    'safety': [
      {
        question: 'How does buyer protection work?',
        answer: 'When you purchase an item, your payment is held in escrow. The seller ships the item, and once you confirm it arrived as described, the funds are released to the seller. If there\'s an issue, you can open a dispute before confirming delivery.'
      },
      {
        question: 'How do I report a suspicious listing or user?',
        answer: 'Click the "Report" button on any listing or user profile. Our moderation team reviews all reports within 24 hours and takes appropriate action.'
      },
      {
        question: 'How can I stay safe when doing local pickups?',
        answer: 'Always meet in well-lit, public locations (police station parking lots are ideal). Bring a friend if possible, and trust your instincts. Never share personal financial information outside the platform.'
      }
    ]
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const toggleFAQ = (category, index) => {
    const faqId = `${category}-${index}`;
    setExpandedFAQ(expandedFAQ === faqId ? null : faqId);
  };

  return (
    <div style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h1 style={titleStyle}>Help Center</h1>
          <p style={subtitleStyle}>
            Find answers to common questions and learn how to get the most out of BabyResell.
          </p>
        </div>

        {/* Search Bar */}
        <div style={searchBoxStyle}>
          <input
            type="text"
            placeholder="Search for help articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={searchInputStyle}
          />
          <div style={searchIconStyle}>
            <Search size={20} />
          </div>
        </div>

        {/* Help Categories */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Browse by Category</h2>
          <div style={categoriesGridStyle}>
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <div
                  key={category.id}
                  style={categoryCardStyle}
                  onClick={() => scrollToSection(category.id)}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.12)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.08)';
                  }}
                >
                  <div style={iconWrapperStyle}>
                    <Icon size={24} color={themeColors.primary} />
                  </div>
                  <h3 style={{ color: themeColors.text, fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
                    {category.title}
                  </h3>
                  <p style={{ color: themeColors.textSecondary, fontSize: '14px', lineHeight: '1.5' }}>
                    {category.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* FAQ Sections */}
        {Object.keys(faqs).map((categoryKey) => {
          const category = categories.find(c => c.id === categoryKey);
          return (
            <div key={categoryKey} id={categoryKey} style={sectionStyle}>
              <h2 style={sectionTitleStyle}>{category.title} FAQs</h2>
              {faqs[categoryKey].map((faq, index) => {
                const faqId = `${categoryKey}-${index}`;
                const isExpanded = expandedFAQ === faqId;
                return (
                  <div
                    key={index}
                    style={faqItemStyle}
                    onClick={() => toggleFAQ(categoryKey, index)}
                    onMouseOver={(e) => {
                      e.currentTarget.style.boxShadow = '0 2px 12px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={faqQuestionStyle}>
                      <span>{faq.question}</span>
                      {isExpanded ? (
                        <ChevronUp size={20} color={themeColors.primary} />
                      ) : (
                        <ChevronDown size={20} color={themeColors.textSecondary} />
                      )}
                    </div>
                    {isExpanded && (
                      <div style={faqAnswerStyle}>
                        {faq.answer}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* Still Need Help? */}
        <div style={sectionStyle}>
          <div style={ctaCardStyle}>
            <h2 style={{ color: themeColors.text, fontSize: '24px', fontWeight: 'bold', marginBottom: '12px' }}>
              Still Need Help?
            </h2>
            <p style={{ color: themeColors.textSecondary, fontSize: '16px', marginBottom: '8px' }}>
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <button
              style={buttonStyle}
              onClick={() => navigate('/contact')}
              onMouseOver={(e) => e.target.style.opacity = '0.9'}
              onMouseOut={(e) => e.target.style.opacity = '1'}
            >
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
